import React, { useMemo, useState, useRef } from 'react';
import { Card, CardContent } from '../ui/card';
import { cn } from '../../lib/utils';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LineChart,
} from 'recharts';
import { Users, Layers, ArrowUpRight, ArrowDownRight, Info, ChevronUp, ChevronDown, Download, FileText, Loader2, GitCompare } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

function fmt$(v) {
  const n = Number(v) || 0;
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${n < 0 ? '-' : ''}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${n < 0 ? '-' : ''}$${(abs / 1_000).toFixed(0)}K`;
  return `${n < 0 ? '-' : ''}$${abs.toFixed(0)}`;
}

function roiBand(m) {
  if (m >= 5) return { label: 'Excellent', chip: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300', dot: '#10b981' };
  if (m >= 3) return { label: 'Strong', chip: 'bg-teal-100 text-teal-700 dark:bg-teal-500/15 dark:text-teal-300', dot: '#14b8a6' };
  if (m >= 1) return { label: 'Watch', chip: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300', dot: '#f59e0b' };
  return { label: 'Critical', chip: 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300', dot: '#f43f5e' };
}

function monthKey(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// Per-group monthly Net-ROI series (last 12 months) for the row sparkline.
function monthlyRoiSeries(scns) {
  const buckets = new Map();
  for (const s of scns) {
    const mk = monthKey(s.date);
    if (!mk) continue;
    const inv = Number(s.investment) || 0;
    const b = buckets.get(mk) || { key: mk, inv: 0, fi: 0 };
    b.inv += inv;
    b.fi += Number(s.financial_impact) || (inv + (Number(s.net_benefit) || 0));
    buckets.set(mk, b);
  }
  return [...buckets.values()]
    .sort((a, b) => a.key.localeCompare(b.key))
    .slice(-12)
    .map((b) => ({ v: b.inv > 0 ? +(b.fi / b.inv).toFixed(2) : 0 }));
}

function RowSpark({ data }) {
  if (!data || data.length < 2) return <span className="text-xs text-slate-300 dark:text-slate-600">—</span>;
  const up = data[data.length - 1].v >= data[0].v;
  return (
    <div style={{ width: 84, height: 26 }} className="inline-block align-middle">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 3, right: 2, left: 2, bottom: 3 }}>
          <Line type="monotone" dataKey="v" stroke={up ? '#10b981' : '#f43f5e'} strokeWidth={1.75} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function RoiCohortPrograms({ scenarios = [], filters = {}, selectedOrg, navigate }) {
  const [mode, setMode] = useState('programs'); // 'programs' | 'cohorts'
  const [selected, setSelected] = useState(null); // Set of group keys, null = all
  const [sortKey, setSortKey] = useState('roi');
  const [sortDir, setSortDir] = useState('desc');
  const [exporting, setExporting] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [cmpA, setCmpA] = useState(null);
  const [cmpB, setCmpB] = useState(null);
  const boardRef = useRef(null);

  const cohortNames = useMemo(() => {
    const m = {};
    (filters.cohorts || []).forEach((c) => { m[c.id] = c.name; });
    return m;
  }, [filters.cohorts]);

  // Group scenarios by program (name) or cohort (cohort_id).
  const groups = useMemo(() => {
    const map = new Map();
    for (const s of scenarios) {
      const key = mode === 'cohorts' ? (s.cohort_id || '__unassigned') : (s.name || 'Untitled');
      const label = mode === 'cohorts'
        ? (s.cohort_id ? (cohortNames[s.cohort_id] || s.cohort_id) : 'Unassigned')
        : (s.name || 'Untitled');
      const g = map.get(key) || { key, label, scenarios: [], inv: 0, nb: 0, fi: 0, count: 0 };
      const inv = Number(s.investment) || 0;
      const nb = Number(s.net_benefit) || 0;
      const fi = Number(s.financial_impact) || (inv + nb);
      g.scenarios.push(s);
      g.inv += inv; g.nb += nb; g.fi += fi; g.count += 1;
      map.set(key, g);
    }
    return [...map.values()].map((g) => ({ ...g, roi: g.inv > 0 ? +(g.fi / g.inv).toFixed(2) : 0, trend: monthlyRoiSeries(g.scenarios) }));
  }, [scenarios, mode, cohortNames]);

  // Reset selection when mode changes.
  const modeGroupKeys = groups.map((g) => g.key).join('|');
  React.useEffect(() => {
    setSelected(null);
    setCmpA(groups[0]?.key ?? null);
    setCmpB(groups[1]?.key ?? groups[0]?.key ?? null);
  }, [mode, modeGroupKeys]);

  const groupByKey = useMemo(() => {
    const m = {};
    groups.forEach((g) => { m[g.key] = g; });
    return m;
  }, [groups]);

  // Aligned monthly ROI series for the two compared groups.
  const compareSeries = useMemo(() => {
    const a = groupByKey[cmpA];
    const b = groupByKey[cmpB];
    if (!a && !b) return [];
    const byMonth = new Map();
    const add = (scns, field) => {
      const buckets = new Map();
      for (const s of scns || []) {
        const mk = monthKey(s.date);
        if (!mk) continue;
        const inv = Number(s.investment) || 0;
        const bk = buckets.get(mk) || { inv: 0, fi: 0 };
        bk.inv += inv;
        bk.fi += Number(s.financial_impact) || (inv + (Number(s.net_benefit) || 0));
        buckets.set(mk, bk);
      }
      for (const [mk, bk] of buckets) {
        const row = byMonth.get(mk) || { month: mk.slice(2).replace('-', '/') };
        row[field] = bk.inv > 0 ? +(bk.fi / bk.inv).toFixed(2) : 0;
        byMonth.set(mk, row);
      }
    };
    add(a?.scenarios, 'A');
    add(b?.scenarios, 'B');
    return [...byMonth.entries()].sort((x, y) => x[0].localeCompare(y[0])).map(([, v]) => v).slice(-12);
  }, [groupByKey, cmpA, cmpB]);

  const isSelected = (key) => selected === null || selected.has(key);
  const toggle = (key) => {
    setSelected((prev) => {
      const base = prev === null ? new Set(groups.map((g) => g.key)) : new Set(prev);
      if (base.has(key)) base.delete(key); else base.add(key);
      if (base.size === 0) return null; // never leave an empty dead-end — revert to All
      if (base.size === groups.length) return null;
      return base;
    });
  };

  const activeGroups = groups.filter((g) => isSelected(g.key));

  const sorted = useMemo(() => {
    const arr = [...activeGroups];
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      const av = sortKey === 'label' ? a.label.toLowerCase() : a[sortKey];
      const bv = sortKey === 'label' ? b.label.toLowerCase() : b[sortKey];
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return arr;
  }, [activeGroups, sortKey, sortDir]);

  // Monthly inputs vs outputs across the active groups (last 12 months).
  const monthly = useMemo(() => {
    const buckets = new Map();
    for (const g of activeGroups) {
      for (const s of g.scenarios) {
        const mk = monthKey(s.date);
        if (!mk) continue;
        const b = buckets.get(mk) || { key: mk, inv: 0, nb: 0, fi: 0 };
        const inv = Number(s.investment) || 0;
        const nb = Number(s.net_benefit) || 0;
        b.inv += inv; b.nb += nb; b.fi += Number(s.financial_impact) || (inv + nb);
        buckets.set(mk, b);
      }
    }
    return [...buckets.values()]
      .sort((a, b) => a.key.localeCompare(b.key))
      .slice(-12)
      .map((b) => ({
        month: b.key.slice(2).replace('-', "/"),
        Investment: Math.round(b.inv),
        'Net Benefit': Math.round(b.nb),
        ROI: b.inv > 0 ? +(b.fi / b.inv).toFixed(2) : 0,
      }));
  }, [activeGroups]);

  // Summary strip scoped to the active selection.
  const totals = useMemo(() => {
    const inv = activeGroups.reduce((a, g) => a + g.inv, 0);
    const nb = activeGroups.reduce((a, g) => a + g.nb, 0);
    const fi = activeGroups.reduce((a, g) => a + g.fi, 0);
    const withRoi = activeGroups.filter((g) => g.inv > 0);
    const best = withRoi.length ? withRoi.reduce((a, g) => (g.roi > a.roi ? g : a)) : null;
    const worst = withRoi.length ? withRoi.reduce((a, g) => (g.roi < a.roi ? g : a)) : null;
    return { inv, nb, roi: inv > 0 ? +(fi / inv).toFixed(2) : 0, best, worst };
  }, [activeGroups]);

  const hasUnassigned = mode === 'cohorts' && groups.some((g) => g.key === '__unassigned');

  const setSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'label' ? 'asc' : 'desc'); }
  };
  const SortIcon = ({ col }) => sortKey !== col ? null
    : (sortDir === 'asc' ? <ChevronUp className="inline h-3 w-3" /> : <ChevronDown className="inline h-3 w-3" />);

  const exportCSV = () => {
    const entityLabel = mode === 'cohorts' ? 'Cohort' : 'Program';
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = [entityLabel, 'Investment', 'Net Benefit', 'Net ROI (x)', 'Cases'];
    const lines = [header.join(',')];
    sorted.forEach((g) => lines.push([esc(g.label), Math.round(g.inv), Math.round(g.nb), g.roi, g.count].join(',')));
    lines.push('');
    lines.push([esc('TOTAL'), Math.round(totals.inv), Math.round(totals.nb), totals.roi, activeGroups.reduce((a, g) => a + g.count, 0)].join(','));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `financial-health-${mode}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = async () => {
    if (!boardRef.current) return;
    setExporting(true);
    try {
      const isDark = document.documentElement.classList.contains('dark');
      const canvas = await html2canvas(boardRef.current, { scale: 2, backgroundColor: isDark ? '#0a0f1c' : '#ffffff', useCORS: true });
      const img = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgH = (canvas.height * pageW) / canvas.width;
      let heightLeft = imgH;
      let position = 0;
      pdf.addImage(img, 'PNG', 0, position, pageW, imgH);
      heightLeft -= pageH;
      while (heightLeft > 0) {
        position -= pageH;
        pdf.addPage();
        pdf.addImage(img, 'PNG', 0, position, pageW, imgH);
        heightLeft -= pageH;
      }
      pdf.save(`financial-health-${mode}-${new Date().toISOString().slice(0, 10)}.pdf`);
    } finally {
      setExporting(false);
    }
  };

  if (!scenarios.length) {
    return (
      <Card className="border-dashed border-2 border-slate-200 dark:border-white/10" data-testid="roi-cohorts-programs-empty">
        <CardContent className="py-16 text-center">
          <Layers className="h-12 w-12 mx-auto text-slate-300 mb-3" />
          <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-200">No cohort or program data yet</h3>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            Build business cases in the Business Case Builder to see financial health per cohort and program here.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5" data-testid="roi-cohorts-programs" ref={boardRef}>
      {/* Header + view toggle */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-900 dark:text-slate-50">Cohort &amp; Programs</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">Financial health (ROI) per {mode === 'cohorts' ? 'cohort' : 'program'} — inputs vs outputs and net ROI</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-slate-200 dark:border-white/10 p-0.5 bg-white dark:bg-white/5" data-testid="roi-cp-view-toggle">
            {[['programs', 'Programs', Layers], ['cohorts', 'Cohorts', Users]].map(([val, lbl, Icon]) => (
              <button
                key={val}
                onClick={() => setMode(val)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors',
                  mode === val ? 'bg-[#44b6d2] text-[#04222b]' : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200',
                )}
                data-testid={`roi-cp-view-${val}`}
              >
                <Icon className="h-3.5 w-3.5" /> {lbl}
              </button>
            ))}
          </div>
          <button
            onClick={() => setCompareMode((v) => !v)}
            className={cn('inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors',
              compareMode ? 'border-[#44b6d2] bg-[#44b6d2]/10 text-[#0b6b82] dark:text-[#7fd4e8]' : 'border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300 hover:border-slate-300 dark:hover:border-white/20')}
            data-testid="roi-cp-compare-toggle"
            data-html2canvas-ignore="true"
            title="Compare two entities side by side"
          ><GitCompare className="h-3.5 w-3.5" /> Compare</button>
          <button
            onClick={exportCSV}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-white/10 px-3 py-1.5 text-sm font-semibold text-slate-600 dark:text-slate-300 hover:border-slate-300 dark:hover:border-white/20 transition-colors"
            data-testid="roi-cp-export-csv"
            data-html2canvas-ignore="true"
            title="Download the leaderboard as CSV"
          ><Download className="h-3.5 w-3.5" /> CSV</button>
          <button
            onClick={exportPDF}
            disabled={exporting}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-white/10 px-3 py-1.5 text-sm font-semibold text-slate-600 dark:text-slate-300 hover:border-slate-300 dark:hover:border-white/20 transition-colors disabled:opacity-60"
            data-testid="roi-cp-export-pdf"
            data-html2canvas-ignore="true"
            title="Export this board as a PDF for the finance pack"
          >{exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />} PDF</button>
        </div>
      </div>

      {hasUnassigned && (
        <p className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400" data-testid="roi-cp-partial-note">
          <Info className="h-3.5 w-3.5" /> Some business cases aren’t tagged to a cohort — shown as “Unassigned”. Program-level figures are complete.
        </p>
      )}

      {/* Multi-select chips */}
      {groups.length > 1 && (
        <div className="flex flex-wrap gap-2" data-testid="roi-cp-filter-chips">
          <button
            onClick={() => setSelected(null)}
            className={cn('rounded-full border px-3 py-1 text-xs font-semibold transition-colors',
              selected === null ? 'border-[#44b6d2] bg-[#44b6d2]/10 text-[#0b6b82] dark:text-[#7fd4e8]' : 'border-slate-200 dark:border-white/10 text-slate-500 dark:text-slate-400')}
            data-testid="roi-cp-chip-all"
          >All</button>
          {groups.map((g) => (
            <button
              key={g.key}
              onClick={() => toggle(g.key)}
              className={cn('rounded-full border px-3 py-1 text-xs font-semibold transition-colors max-w-[220px] truncate',
                isSelected(g.key) ? 'border-slate-300 dark:border-white/20 bg-white dark:bg-white/10 text-slate-700 dark:text-slate-200' : 'border-slate-200 dark:border-white/5 text-slate-400 dark:text-slate-500 line-through')}
              title={g.label}
              data-testid={`roi-cp-chip-${g.key}`}
            >{g.label}</button>
          ))}
        </div>
      )}

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="roi-cp-summary-strip">
        {[
          { label: 'Total Investment', value: fmt$(totals.inv), testid: 'inv' },
          { label: 'Total Net Benefit', value: fmt$(totals.nb), testid: 'nb' },
          { label: 'Blended ROI', value: `${totals.roi}x`, testid: 'roi' },
          { label: `Best ${mode === 'cohorts' ? 'Cohort' : 'Program'}`, value: totals.best ? `${totals.best.roi}x` : '—', hint: totals.best?.label, testid: 'best' },
        ].map((s) => (
          <Card key={s.testid} className="border-slate-200 dark:border-white/10 dark:bg-white/5">
            <CardContent className="py-3 px-4">
              <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500">{s.label}</p>
              <p className="text-xl font-extrabold text-slate-900 dark:text-slate-50 tabular-nums" data-testid={`roi-cp-summary-${s.testid}`}>{s.value}</p>
              {s.hint && <p className="text-[11px] text-slate-400 truncate">{s.hint}</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Compare — two entities side by side */}
      {compareMode && groups.length > 1 && (() => {
        const A = groupByKey[cmpA];
        const B = groupByKey[cmpB];
        const Picker = ({ value, onChange, testid }) => (
          <select
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className="rounded-md border border-slate-200 dark:border-white/15 bg-white dark:bg-white/10 px-2 py-1 text-sm text-slate-800 dark:text-slate-100 max-w-[220px]"
            data-testid={testid}
          >
            {groups.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
          </select>
        );
        const MetricRow = ({ label, a, b, fmt }) => (
          <div className="grid grid-cols-3 items-center py-1.5 border-b border-slate-100 dark:border-white/5 text-sm">
            <span className="text-slate-500 dark:text-slate-400">{label}</span>
            <span className="text-right font-semibold text-slate-800 dark:text-slate-100 tabular-nums">{fmt(a)}</span>
            <span className="text-right font-semibold text-slate-800 dark:text-slate-100 tabular-nums">{fmt(b)}</span>
          </div>
        );
        return (
          <Card className="border-[#44b6d2]/30 dark:bg-white/5" data-testid="roi-cp-compare-panel">
            <CardContent className="pt-4">
              <div className="grid grid-cols-3 items-end gap-2 mb-2">
                <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">Side-by-side compare</h3>
                <Picker value={cmpA} onChange={setCmpA} testid="roi-cp-compare-a" />
                <Picker value={cmpB} onChange={setCmpB} testid="roi-cp-compare-b" />
              </div>
              {A && B && (
                <>
                  <MetricRow label="Investment" a={A.inv} b={B.inv} fmt={fmt$} />
                  <MetricRow label="Net Benefit" a={A.nb} b={B.nb} fmt={fmt$} />
                  <MetricRow label="Net ROI" a={A.roi} b={B.roi} fmt={(v) => `${v}x`} />
                  <MetricRow label="Business cases" a={A.count} b={B.count} fmt={(v) => v} />
                  <div className="mt-3">
                    <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1">12-mo Net-ROI trend</p>
                    {compareSeries.length >= 2 ? (
                      <div style={{ height: 200 }} data-testid="roi-cp-compare-chart">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={compareSeries} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#94a3b833" />
                            <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                            <YAxis tickFormatter={(v) => `${v}x`} tick={{ fontSize: 11 }} stroke="#94a3b8" width={40} />
                            <Tooltip contentStyle={{ background: '#0b1220', border: '1px solid #22304d', borderRadius: 8, fontSize: 12 }} formatter={(v) => `${v}x`} />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Line type="monotone" dataKey="A" name={A.label} stroke="#06b6d4" strokeWidth={2.5} dot={{ r: 2 }} connectNulls />
                            <Line type="monotone" dataKey="B" name={B.label} stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 2 }} connectNulls />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <p className="py-6 text-center text-sm text-slate-400">Not enough dated cases to chart a trend for this pair.</p>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Inputs vs Outputs over time */}
      <Card className="border-slate-200 dark:border-white/10 dark:bg-white/5">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">Inputs vs Outputs — up to 12 months</h3>
            <span className="text-[11px] text-slate-400">Bars: Investment vs Net Benefit · Line: Net ROI</span>
          </div>
          {monthly.length ? (
            <div style={{ height: 300 }} data-testid="roi-cp-io-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={monthly} margin={{ top: 8, right: 8, left: 8, bottom: 0 }} barCategoryGap="25%" barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#94a3b833" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <YAxis yAxisId="left" tickFormatter={fmt$} tick={{ fontSize: 11 }} stroke="#94a3b8" width={54} />
                  <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${v}x`} tick={{ fontSize: 11 }} stroke="#94a3b8" width={40} />
                  <Tooltip
                    contentStyle={{ background: '#0b1220', border: '1px solid #22304d', borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: '#e2e8f0' }}
                    formatter={(v, n) => [n === 'ROI' ? `${v}x` : fmt$(v), n]}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar yAxisId="left" dataKey="Investment" fill="#6366f1" radius={[3, 3, 0, 0]} maxBarSize={48} />
                  <Bar yAxisId="left" dataKey="Net Benefit" fill="#06b6d4" radius={[3, 3, 0, 0]} maxBarSize={48} />
                  <Line yAxisId="right" type="monotone" dataKey="ROI" stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="py-10 text-center text-sm text-slate-400">No dated business cases in the selected range.</p>
          )}
        </CardContent>
      </Card>

      {/* Leaderboard */}
      <Card className="border-slate-200 dark:border-white/10 dark:bg-white/5">
        <CardContent className="pt-4 overflow-x-auto">
          <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100 mb-3">{mode === 'cohorts' ? 'Cohort' : 'Program'} ROI leaderboard</h3>
          <table className="w-full text-sm" data-testid="roi-cp-leaderboard">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-200 dark:border-white/10">
                <th className="py-2 pr-3 cursor-pointer" onClick={() => setSort('label')}>{mode === 'cohorts' ? 'Cohort' : 'Program'} <SortIcon col="label" /></th>
                <th className="py-2 px-3 text-right cursor-pointer" onClick={() => setSort('inv')}>Investment <SortIcon col="inv" /></th>
                <th className="py-2 px-3 text-right cursor-pointer" onClick={() => setSort('nb')}>Net Benefit <SortIcon col="nb" /></th>
                <th className="py-2 px-3 text-right cursor-pointer" onClick={() => setSort('roi')}>Net ROI <SortIcon col="roi" /></th>
                <th className="py-2 px-3 text-center">12-mo trend</th>
                <th className="py-2 px-3 text-right cursor-pointer" onClick={() => setSort('count')}>Cases <SortIcon col="count" /></th>
                <th className="py-2 pl-3 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((g) => {
                const band = roiBand(g.roi);
                return (
                  <tr key={g.key} className="border-b border-slate-100 dark:border-white/5 hover:bg-slate-50 dark:hover:bg-white/5" data-testid={`roi-cp-row-${g.key}`}>
                    <td className="py-2.5 pr-3 font-semibold text-slate-800 dark:text-slate-100 max-w-[280px] truncate" title={g.label}>{g.label}</td>
                    <td className="py-2.5 px-3 text-right tabular-nums text-slate-600 dark:text-slate-300">{fmt$(g.inv)}</td>
                    <td className={cn('py-2.5 px-3 text-right tabular-nums font-semibold', g.nb >= 0 ? 'text-slate-800 dark:text-slate-100' : 'text-rose-600 dark:text-rose-400')}>{fmt$(g.nb)}</td>
                    <td className="py-2.5 px-3 text-right">
                      <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold tabular-nums', band.chip)}>
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: band.dot }} />{g.roi}x
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-center" data-testid={`roi-cp-spark-${g.key}`}><RowSpark data={g.trend} /></td>
                    <td className="py-2.5 px-3 text-right tabular-nums text-slate-500">{g.count}</td>
                    <td className="py-2.5 pl-3 text-right">
                      <button
                        onClick={() => navigate(mode === 'programs'
                          ? `/roi-health/cases?program=${encodeURIComponent(g.label)}`
                          : `/roi-health/cases?cohort=${encodeURIComponent(g.key)}`)}
                        className="text-xs font-semibold text-[#0b6b82] dark:text-[#7fd4e8] hover:underline inline-flex items-center gap-0.5"
                        data-testid={`roi-cp-view-cases-${g.key}`}
                      >Cases <ArrowUpRight className="h-3 w-3" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {totals.worst && totals.worst.roi < 1 && (
            <p className="mt-3 flex items-center gap-1.5 text-xs text-rose-600 dark:text-rose-400" data-testid="roi-cp-worst-callout">
              <ArrowDownRight className="h-3.5 w-3.5" />
              <span className="font-semibold">{totals.worst.label}</span> is your lowest performer at {totals.worst.roi}x —{' '}
              <button onClick={() => navigate('/roi-health/priorities')} className="underline font-semibold">model the lift</button>.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
